import { lazy } from "react";
import { Navigate, createBrowserRouter } from "react-router-dom";
import Layouts from "../layouts/index";
import MenuLayout from "../pages/MenuLayout";

const RtqList = lazy(() => import("../pages/rtq/RtqList"));
const RtqWizard = lazy(() => import("../pages/rtq/RtqWizard"));
const WorkFlow = lazy(() => import("../pages/WorkFlow"));
const LinkedProducts = lazy(() => import("../pages/LinkedProducts"));
const LinkedSubmissions = lazy(() => import("../pages/LinkedSubmissions"));
const QADetails = lazy(() => import("../pages/QADetails"));
const AddQuestion = lazy(() => import("../pages/rtq/QueryTab/AddQuestion"));
const AddResponse = lazy(() => import("../pages/rtq/QueryTab/AddResponse"));
const ManageQuestionAttachment = lazy(
  () => import("../pages/rtq/QueryTab/ManageQuestionAttachment"),
);
const ManageResponseAttachment = lazy(
  () => import("../pages/rtq/QueryTab/ManageResponseAttachment"),
);
const CategoryGroup = lazy(() => import("../pages/CategoryGroup"));
const Documents = lazy(() => import("../pages/Documents"));
const DynamicListPage = lazy(() => import("../pages/rtq/dynamic/ListPage"));
const DynamicEditPage = lazy(() => import("../pages/rtq/dynamic/EditPage"));
const DynamicViewPage = lazy(() => import("../pages/rtq/dynamic/ViewPage"));

const GlobalSearchList = lazy(() => import("../pages/globalSearch/index"));
const RtqDetailLayout = lazy(() => import("../pages/RtqDetailLayout"));
const RtqDetailEntry = lazy(() => import("../pages/RtqDetailEntry"));
const LegacyRedirect = lazy(() => import("../pages/LegacyRedirect"));
const LegacyRedirectByQueryId = lazy(
  () => import("../pages/LegacyRedirectByQueryId"),
);

const LinkRecords = lazy(() => import("../pages/rtq/LinkRecords"));
const ImpactedRecords = lazy(() => import("../pages/rtq/ImpactedRecords"));
const TriggeringRecords = lazy(
  () => import("../pages/rtq/TriggeringRecords"),
);

const AssociateProcessPage = lazy(
  () => import("../pages/newWorkFlow/AssociateProcessPage"),
);
const InitiateProcessPage = lazy(
  () => import("../pages/newWorkFlow/InitiateProcessPage"),
);
const RecordMetadata = lazy(() => import("../pages/rtq/RecordMetadata"));

const EditRecordMetadata = lazy(
  () => import("../pages/rtq/RecordMetadata/EditRecordMetadata"),
);

// HAQ Agent Import
const RtqImportPage = lazy(() => import("../pages/rtq/RtqImport/RtqImportPage"));
const RtqImportReviewPage = lazy(() => import("../pages/rtq/RtqImport/RtqImportReviewPage"));

const routesForm = [
  {
    path: "/*",
    element: <Navigate to="/rtqs" />,
  },
  {
    path: "/rtqs",
    element: <Layouts />,
    children: [
      {
        path: "associate-process/:rtqId/:recordId",
        element: <AssociateProcessPage />,
      },
      {
        path: "initiate-process/:rtqId/:processAssociationId/:recordId",
        element: <InitiateProcessPage />,
      },
      {
        path: "",
        element: <RtqList />,
      },
      {
        path: "import",
        element: <RtqImportPage />,
      },
      {
        path: "import/review",
        element: <RtqImportReviewPage />,
      },
      {
        path: "/rtqs/add-rtq-wizard",
        element: <RtqWizard />,
      },
      // {
      //   path: "/rtqs/add-rtq",
      //   element: <RtqAdd />,
      // },
      {
        path: "/rtqs/edit-rtq/:rtqId",
        element: <Navigate to="basic-information" />,
      },
      {
        path: "/rtqs/view-rtq/:rtqId",
        element: <Navigate to="basic-information" />,
      },
      {
        path: "/rtqs/:detailEditMode/:rtqId",
        element: <RtqDetailLayout />,
        children: [
          {
            path: "basic-information",
            element: <RtqDetailEntry />,
            handle: {
              breadcrumbLevel: "basicInfo",
            },
          },
          {
            path: "default-workflow",
            element: <WorkFlow />,
          },
          {
            path: "linked-records",
            element: <LinkRecords />,
          },
          {
            path: "impacted-records",
            element: <ImpactedRecords />,
          },
          {
            path: "triggering-records",
            element: <TriggeringRecords />,
          },
          {
            path: "categories",
            element: <CategoryGroup />,
          },
          {
            path: "documents-list",
            element: <Documents />,
          },
          {
            path: "documents",
            element: <Documents />,
          },
          {
            path: "linked-products",
            element: <LinkedProducts />,
          },
          {
            path: "linked-submissions",
            element: <LinkedSubmissions />,
          },
          {
            path: "record-metadata",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <RecordMetadata />,
              },
              {
                path: "addmetadata/:docId",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "editmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "viewmetadata/:docId/:id",
                element: <EditRecordMetadata />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
          {
            path: ":menuId",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <DynamicListPage />,
                handle: {
                  breadcrumbLevel: "grid",
                },
              },
              {
                path: "add",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "add",
                },
              },
              {
                path: "edit/:id",
                element: <DynamicEditPage />,
                handle: {
                  breadcrumbLevel: "edit",
                },
              },
              {
                path: "view/:id",
                element: <DynamicViewPage />,
                handle: {
                  breadcrumbLevel: "view",
                },
              },
            ],
          },
          {
            path: "question-response-details",
            element: <MenuLayout />,
            children: [
              {
                index: true,
                element: <QADetails />,
              },
              {
                path: ":queryId",
                element: <LegacyRedirectByQueryId />,
              },
              {
                path: "add-question",
                element: <AddQuestion />,
                handle: {
                  pageLevel: "question-detail",
                  breadcrumbLevel: "add",
                },
              },
              {
                path: ":questionEditMode/:questionId",
                element: <MenuLayout />,
                children: [
                  {
                    index: true,
                    element: <AddQuestion />,
                    handle: {
                      pageLevel: "question-detail",
                    },
                  },
                  {
                    path: "manage-attachment",
                    element: <ManageQuestionAttachment />,
                    handle: {
                      pageLevel: "question-detail-attachment",
                    },
                  },
                ],
              },
              {
                path: ":questionId/add-response",
                element: <AddResponse />,
                handle: {
                  pageLevel: "response-detail",
                  breadcrumbLevel: "add",
                },
              },
              {
                path: ":questionId/:responseEditMode/:responseId",
                element: <MenuLayout />,
                children: [
                  {
                    index: true,
                    element: <AddResponse />,
                    handle: {
                      pageLevel: "response-detail",
                    },
                  },
                  {
                    path: "manage-attachment",
                    element: <ManageResponseAttachment />,
                    handle: {
                      // breadcrumbLevel: "add",
                      pageLevel: "response-detail-attachment",
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "/rtqs/edit-rtq*",
        element: <LegacyRedirect />,
      },
      {
        path: "/rtqs/view-rtq*",
        element: <LegacyRedirect />,
      },
    ],
  },
  {
    path: "/global-search-list",
    element: <Layouts />,
    children: [
      {
        path: "/global-search-list",
        element: <GlobalSearchList />,
      },
    ],
  },
];

const Router = createBrowserRouter(routesForm);

export { Router };
